import { useContext } from 'react';
import styled from 'styled-components';
import { CTA, Text, ThemeProvider, TokenTextAppearance } from '@volkswagen-onehub/components-core';
import { Trans, useTranslation } from 'react-i18next';
import { DataSourceTypes } from '@org-avp/avp-hck-dga-customerui-commons';
import { minimalScreenWidth } from '@/common/breakpoints';
import { SocialPopover } from '@/components/socialPopover/socialPopover';
import { ConfiguratorPrivate, TestDrive } from '@volkswagen-onehub/icons-core';
import { GiveawayContext } from '@/state/state';
import { useTrackingService } from '@/contexts/TrackingServiceContext';
import { getMarketWebsites, TestDriveData } from '@/common/marketConfig/marketUtils';
import { TrackingEvent } from '@/util/tracking/definitions';
import { DealerId } from '@volkswagen-onehub/mydealer-service';
import { BrandIdentifier } from '@volkswagen-onehub/gfa-dealer-context-service';
import { extractBrand } from '@/util/brand';
import { extractMyDealerId } from '@/util/dealer-data';

const StyledBackground = styled.section`
  background-image: ${(props) => props.theme.colors.gradient.radial};
  padding: 80px 0;
  margin-top: ${(props) => props.theme.size.dynamic0350};
`;

const StyledTextWrapper = styled.div`
  padding: 0 ${(props) => props.theme.size.grid002};
  text-align: center;

  @media screen and ${minimalScreenWidth.l} {
    padding: 0 ${(props) => props.theme.size.grid004};
  }

  @media screen and ${minimalScreenWidth.xl} {
    padding: 0 ${(props) => props.theme.size.grid006};
  }
`;

const StyledHeadline = styled(Text)`
  padding-bottom: ${(props) => props.theme.size.dynamic0100};
`;

const StyledSubheadline = styled(Text)`
  padding-bottom: ${(props) => props.theme.size.dynamic0130};
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: ${(props) => props.theme.size.dynamic0040};
  justify-content: center;
  flex-direction: column;
  align-items: center;

  @media screen and ${minimalScreenWidth.l} {
    flex-direction: row;
    flex: 0 0 auto;
  }
`;

export const CallToActionBox = () => {
  const {
    marketingCode,
    market,
    brand,
    configuration,
    dealerId,
    locale,
    isCertifiedPreOwned,
    stockLink,
    dataSource,
  } = useContext(GiveawayContext);
  const { t } = useTranslation();
  const trackingService = useTrackingService();

  const isVIN = marketingCode === 'VIN';
  const brandInfo = brand ?? extractBrand(marketingCode);
  const myDealerId = extractMyDealerId(dealerId);

  const testDriveDetails: TestDriveData = {
    market,
    id: myDealerId as DealerId,
    brand: brandInfo as BrandIdentifier,
    carlineId: configuration?.carlineId,
  };

  const marketUrls = getMarketWebsites(testDriveDetails, locale, marketingCode, market, brand);

  const trackEvent = (trackingEvent: TrackingEvent, url?: string, name?: string) => {
    switch (trackingEvent) {
      case TrackingEvent.VWBasicButtonClick: {
        trackingService.track(TrackingEvent.VWBasicButtonClick, {
          link: { url, name },
          eventInfo: { contentId: `clicked button: ${name}` },
        });
        break;
      }
      case TrackingEvent.VWBasicLinkClick: {
        trackingService.track(TrackingEvent.VWBasicLinkClick, {
          link: { url, name },
          process: { StepName: `${name}` },
          eventInfo: { contentId: `clicked basic link: ${name}` },
        });
        break;
      }
      default:
        break;
    }
  };

  return (
    <ThemeProvider theme="inverted">
      <StyledBackground>
        <StyledTextWrapper>
          <StyledHeadline appearance={TokenTextAppearance.headline500}>
            <Trans i18nKey="callToActionBox.headline" components={{ strong: <strong /> }} />
          </StyledHeadline>
          <StyledSubheadline appearance={TokenTextAppearance.copy200}>
            {t('callToActionBox.subheadline')}
          </StyledSubheadline>
          <ButtonContainer>
            {marketUrls.configurator &&
              isCertifiedPreOwned !== true &&
              dataSource !== DataSourceTypes.CARLOS && (
                <CTA
                  aria-label={isVIN ? t(`sideBar.create`) : t(`sideBar.adjust`)}
                  icon={<ConfiguratorPrivate aria-hidden />}
                  tag="a"
                  href={marketUrls.configurator}
                  target="_blank"
                  data-testid="configurator-link"
                  onClick={() =>
                    trackEvent(
                      TrackingEvent.VWBasicButtonClick,
                      marketUrls.configurator,
                      isVIN ? 'create configuration' : 'adjust configuration',
                    )
                  }
                >
                  {isVIN ? t(`sideBar.create`) : t(`sideBar.adjust`)}
                </CTA>
              )}

            {marketUrls.testdrive && !isVIN && (
              <CTA
                aria-label={t('sideBar.bookTestDrive')}
                emphasis="secondary"
                icon={<TestDrive aria-hidden />}
                iconPosition="start"
                tag="a"
                href={marketUrls.testdrive}
                target="_blank"
                data-testid="testdrive-link"
                onClick={() => {
                  trackEvent(
                    TrackingEvent.VWBasicButtonClick,
                    marketUrls.testdrive,
                    'book testdrive',
                  );
                }}
              >
                {t(`sideBar.bookTestDrive`)}
              </CTA>
            )}

            {isCertifiedPreOwned && stockLink && (
              <CTA
                aria-label={t('sideBar.view')}
                tag="a"
                href={stockLink}
                target="_blank"
                data-testid="viewOffer-link"
                onClick={() =>
                  trackEvent(TrackingEvent.VWBasicButtonClick, stockLink, 'view offer')
                }
              >
                {t('sideBar.view')}
              </CTA>
            )}
            <SocialPopover />
          </ButtonContainer>
        </StyledTextWrapper>
      </StyledBackground>
    </ThemeProvider>
  );
};
